import {
  Button,
  Modal,
  Skeleton,
  stylize,
  Toast,
} from "@envestnet/envreact-component-library";
import React, { useEffect, useState } from "react";
import withIntl from "utils/withIntl";

import { faArrowLeft, faTrashAlt } from "@fortawesome/pro-regular-svg-icons";
import { faCircleCheck, faCircleX } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classnames from "classnames";
import { ToastMessage } from "types/Toast";
import { UserInfo, UserDetails } from "types/UserInfo";
import { Credential } from "./types";
import messages from "./messages";
import styles, { Styles } from "./styles";
import { Authenticators } from "./types";
import { FEATURES } from "scenes/features.contants";

interface InjectedProps extends Styles {
  formatMessage: FormatMessage;

  loading: boolean;
  authenticators: Array<Authenticators>;
  loadAuthenticators: () => void;
  goToDashboard: () => void;
  goToEditPassword: () => void;
  goToDashboardMfa: () => void;
  goToSecuritySettings: () => void;
  toast: ToastMessage | null;
  setToast: (toast: ToastMessage | null) => void;
  userInfo: UserInfo;
  userDetails: UserDetails;
  logout: () => void;
  globalLoading: boolean;
  hasUpdatedTnCAccepted: boolean;
  tncForCidpUser: string | null;
  getTnCForCidpUser: () => void;
  setUserAcceptedNewTnC: () => void;
  fetchPasskeys: () => void;
  passkeys: Array<Credential>;
  deletePasskey: (obj: { credentialId: string }) => void;
  featureFlags: Array<string>;
  goToEditUsername: () => void;
}

interface DefaultProps {
  // Default props should always be required props, i.e. no ? indicating it's optional
  // The default values are specified below
}

export interface DashboardSecuritySettingsLayoutProps extends DefaultProps {
  /** CSS class name to be applied to top level component */
  className?: string;

  /** Inline style object to be applied to top level component */
  style?: React.CSSProperties;

  /** Internal - injected from WithStyles */
  innerRef?: React.RefObject<HTMLDivElement>;
}

const defaultProps: DefaultProps = {};

type Props = InjectedProps & DashboardSecuritySettingsLayoutProps;

const DashboardSecuritySettingsLayout = (props: Props) => {
  const {
    classes,
    formatMessage,
    loadAuthenticators,
    loading,
    authenticators,
    goToDashboard,
    goToEditPassword,
    toast,
    setToast,
    goToDashboardMfa,
    userInfo,
    userDetails,
    globalLoading,
    getTnCForCidpUser,
    passkeys,
    fetchPasskeys,
    deletePasskey,
    featureFlags,
    goToEditUsername,
  } = props;

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [passkeyToDelete, setPasskeyToDelete] = useState<string | null>(null);

  const isPasskeyFeatureFlagEnabled = featureFlags.includes(
    FEATURES.PASSKEY_SUPPORT
  );

  const isUsernameCustomizationEnabled = featureFlags.includes(
    FEATURES.USERNAME_CUSTOMIZATION
  );

  const isEmailOtpEnabled = userDetails.emailOtpEnabled;

  // If userInfo and userDetails are loaded, check if the user has directLoginEnabled.
  // If yes, then continue. Else redirect to the dashboard page.
  useEffect(() => {
    if (userInfo.cidpId !== "" && userDetails.cidpId !== "") {
      if (!userDetails.directLoginEnabled) {
        goToDashboard();
      }

      loadAuthenticators();
      if (isPasskeyFeatureFlagEnabled && userDetails.passkeysEnabled)
        fetchPasskeys();
    }
  }, [userDetails, userInfo, featureFlags]);

  useEffect(() => {
    if ((userInfo as UserInfo).email !== "") {
      getTnCForCidpUser();
    }
  }, [userInfo]);

  const passkeyUrl = `${
    window.config.ISSUER
  }/protocol/openid-connect/auth?client_id=${
    window.config.CLIENT_ID
  }&redirect_uri=${encodeURIComponent(
    `${window.location.origin}/dashboard/securitysettings`
  )}&response_type=code&scope=openid&kc_action=webauthn-register-passwordless`;

  const openPasskeyPopup = () => {
    const w = 550,
      h = 650;
    const left = window.screenX + (window.outerWidth - w) / 2;
    const top = window.screenY + (window.outerHeight - h) / 2;
    const popup = window.open(
      passkeyUrl,
      "passkey-setup",
      `width=${w},height=${h},left=${left},top=${top}`
    );
    const timer = setInterval(() => {
      if (popup?.closed) {
        clearInterval(timer);
        fetchPasskeys();
      }
    }, 500);
  };

  const getLoadingScreen = () => (
    <div className={classes.securitySettings} data-testid="loading-skeletons">
      <div className={classes.mfaPassword}>
        <div className={classes.passwordHeader}>
          <h5
            className={classnames(classes.passwordLabel, "header-font-family")}
          >
            {formatMessage(messages.username)}
          </h5>
        </div>
        <Skeleton animation="wave" height={20} width={200} />
      </div>
      <div className={classes.mfaPassword}>
        <div className={classes.passwordHeader}>
          <h5
            className={classnames(classes.passwordLabel, "header-font-family")}
          >
            {formatMessage(messages.password)}
          </h5>
        </div>
        <Skeleton animation="wave" height={20} width={200} />
      </div>

      <div className={classes.mfaSettings}>
        <div className={classes.mfaSettingsHeaderContainer}>
          <h5 className={classes.mfaSettingsHeader}>
            {formatMessage(messages.mfaSettings)}
          </h5>
        </div>
        <div>
          <Skeleton animation="wave" height={20} width={200} /> <br />
          <Skeleton animation="wave" height={20} width={200} /> <br />
          <Skeleton animation="wave" height={20} width={200} /> <br />
          <Skeleton animation="wave" height={20} width={200} />
        </div>
      </div>

      <div className={classes.mfaPasskey}>
        <div className={classes.passwordHeader}>
          <h5
            className={classnames(classes.passwordLabel, "header-font-family")}
          >
            {formatMessage(messages.passkeys)}
          </h5>
        </div>
        <div>
          <Skeleton animation="wave" height={20} width={200} /> <br />
          <Skeleton animation="wave" height={20} width={200} /> <br />
        </div>
      </div>
    </div>
  );

  const getAuthenticatorScreen = () => (
    <div className={classes.securitySettings}>
      {isUsernameCustomizationEnabled &&
        userDetails.usernameCustomizationEnabled && (
          <div className={classes.mfaUsername}>
            <div className={classes.usernameHeader}>
              <h5
                className={classnames(
                  classes.usernameLabel,
                  "header-font-family"
                )}
              >
                {formatMessage(messages.username)}
              </h5>
              <Button
                buttonStyle="link"
                className={classnames(classes.editLink, "header-font-family")}
                name={formatMessage(messages.edit)}
                onClick={goToEditUsername}
                data-testid="go-to-edit-username-button"
              />
            </div>
            <div className={classes.password}>
              {userInfo.preferred_username}
            </div>
          </div>
        )}

      <div className={classes.mfaPassword}>
        <div className={classes.passwordHeader}>
          <h5
            className={classnames(classes.passwordLabel, "header-font-family")}
          >
            {formatMessage(messages.password)}
          </h5>
          <Button
            buttonStyle="link"
            className={classnames(classes.editLink, "header-font-family")}
            name={formatMessage(messages.edit)}
            onClick={goToEditPassword}
            data-testid="go-to-edit-password-button"
          />
        </div>
        <div className={classes.password}>••••••••••••••</div>
      </div>

      <div className={classes.mfaSettings}>
        <div className={classes.mfaSettingsHeaderContainer}>
          <h5
            className={classnames(
              classes.mfaSettingsHeader,
              "header-font-family"
            )}
          >
            {formatMessage(messages.mfaSettings)}
          </h5>
          <Button
            buttonStyle="link"
            className={classnames(classes.editLink, "header-font-family")}
            name={formatMessage(messages.edit)}
            onClick={() => goToDashboardMfa()}
            data-testid="go-to-mfa-button"
          />
        </div>
        <div>
          {authenticators
            ? authenticators
                .filter(
                  (authenticator) =>
                    isEmailOtpEnabled || authenticator.type !== "email"
                )
                .map((authenticator) => (
                  <div
                    className={classes.authenticator}
                    key={authenticator.name}
                  >
                    <FontAwesomeIcon
                      icon={authenticator.enabled ? faCircleCheck : faCircleX}
                      className={
                        authenticator.enabled
                          ? classes.mfaCheckIcon
                          : classes.mfaCloseIcon
                      }
                    />
                    <span
                      className={classnames(
                        classes.authenticatorName,
                        "header-text-font-family"
                      )}
                    >
                      {formatMessage(messages[authenticator.type])}
                    </span>
                  </div>
                ))
            : null}
        </div>
      </div>
    </div>
  );

  const getPasskeySection = () => (
    <div className={classes.mfaPasskey}>
      <div className={classes.passwordHeader}>
        <h5 className={classnames(classes.passwordLabel, "header-font-family")}>
          {formatMessage(messages.passkeys)}
        </h5>
      </div>
      {/* <div className={classes.authenticator}>
        <a
          href={passkeyUrl}
          className={classnames(classes.editLink, "header-font-family")}
          data-testid="set-up-passkey-link"
        >
          {formatMessage(messages.setUpPasskey)}
        </a>
      </div> */}

      {passkeys.length === 0 && (
        <div className={classes.noPasskeysContainer}>
          {formatMessage(messages.noPasskeys)}
        </div>
      )}

      {passkeys.map((pk) => (
        <div className={classes.authenticator} key={pk.credentialId}>
          <span
            className={classnames(
              classes.passkeyName,
              "header-text-font-family"
            )}
          >
            <span
              className={classnames(classes.passkeyLabel, "header-font-family")}
            >
              {pk.label || pk.credentialId}
            </span>

            <span
              className={classnames(classes.passkeyDate, "header-font-family")}
            >
              {new Date(pk.timestamp).toLocaleDateString("en-US", {
                month: "short",
                day: "2-digit",
                year: "numeric",
              })}
            </span>
            <FontAwesomeIcon
              icon={faTrashAlt}
              className={classes.passkeyDeleteIcon}
              onClick={() => {
                setPasskeyToDelete(pk.credentialId);
                setOpenDeleteDialog(true);
              }}
              data-testid={`delete-passkey-${pk.credentialId}`}
            />
          </span>
        </div>
      ))}

      <div className={classes.authenticator}>
        <Button
          buttonStyle="secondary"
          className={classnames(classes.addPasskeyButton, "header-font-family")}
          name={formatMessage(messages.addPasskey)}
          onClick={openPasskeyPopup}
          data-testid="set-up-passkey-popup-button"
        />
      </div>
    </div>
  );
  return (
    <div className={classes.mainSectionParent}>
      <div className={classes.content}>
        <div>
          <header className={classes.header}>
            <div>
              <span
                className={classnames(classes.heading, "header-font-family")}
              >
                {formatMessage(messages.securitySettings)}
              </span>
            </div>
          </header>
          {/* screen goes here */}
          {loading || globalLoading ? getLoadingScreen() : null}
          {!(loading || globalLoading) && authenticators
            ? getAuthenticatorScreen()
            : null}
          {!(loading || globalLoading) &&
          isPasskeyFeatureFlagEnabled &&
          userDetails.passkeysEnabled
            ? getPasskeySection()
            : null}
        </div>
        <div className={classes.buttonContainer}>
          <Button
            buttonStyle="tertiary"
            iconLeft={faArrowLeft}
            name={formatMessage(messages.backToDashboard)}
            onClick={goToDashboard}
            className={classnames(
              classes.goToDashboardButton,
              "header-font-family"
            )}
            data-testid="close-settings-button"
          />
        </div>
      </div>
      <></>

      {toast?.message && (
        <Toast
          onClose={() => setToast(null)}
          style={{
            width: "300px",
            marginLeft: "36px",
          }}
          variant="white"
          alignment={{
            horizontal: "left",
            vertical: "bottom",
          }}
          {...toast}
        />
      )}

      <Modal
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        contentContainerStyle={{ borderRadius: 0 }}
      >
        <div className={classes.deleteModalContent}>
          <h5 className={classes.deleteModalTitle}>
            {formatMessage(messages.deletePasskey)}
          </h5>
          <p className={classes.deleteModalBody}>
            {formatMessage(messages.deletePasskeyConfirmation)}
          </p>
          <div className={classes.deleteModalActions}>
            <Button
              buttonStyle="secondary"
              name={formatMessage(messages.cancelDelete)}
              onClick={() => setOpenDeleteDialog(false)}
              data-testid="cancel-delete-passkey"
            />
            <Button
              buttonStyle="primarywarning"
              name={formatMessage(messages.confirmDelete)}
              onClick={() => {
                if (passkeyToDelete) {
                  deletePasskey({ credentialId: passkeyToDelete });
                }
                setPasskeyToDelete(null);
                setOpenDeleteDialog(false);
              }}
              data-testid="confirm-delete-passkey"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

DashboardSecuritySettingsLayout.defaultProps = defaultProps;

export default withIntl(
  stylize<Props, DefaultProps>(styles)(DashboardSecuritySettingsLayout)
);
